import { useMemo } from 'react';
import { useMMGRSettings } from '../hoc/withMMGRSettings';
import { HttpClient } from '@wix/http-client';
import { createMediaManagerApi } from '@wix/media-manager-http-api';
import type { MediaManagerApi, MediaManagerApiMethod, IMediaManagerSDK } from '@wix/media-manager-http-api';
import { captureException } from '@sentry/react';
import { fedopsLogger } from '../../services/loggers/fedopsLogger';
import { FedOpsInteraction } from '../../constants/FedOpsInteraction';
import type { ErrorWithData } from '../../monitoring/sentry';
import { mediaGalleryServerCallWithSuccessFailResult } from '@wix/bi-logger-media-data/v2';
import { bi } from '../../services/loggers/bi';
import { stringifyError } from '../../services/loggers/errorLogger/utils';
import type { Source } from '../../constants/Source';

export { MediaManagerApi };

const parsingInteractions = {
  listItems: FedOpsInteraction.ParseListItems,
  searchItems: FedOpsInteraction.ParseSearchItems,
  listFolders: FedOpsInteraction.ParseListFolders,
  privateItemInfo: FedOpsInteraction.ParsePrivateItemInfo,
} as Record<MediaManagerApiMethod, FedOpsInteraction>;

const requestsInteractions = {
  listItems: FedOpsInteraction.PlatformizedListItems,
  searchItems: FedOpsInteraction.PlatformizedSearchItems,
  listFolders: FedOpsInteraction.PlatformizedListFolders,
  privateItemInfo: FedOpsInteraction.PlatformizedPrivateItemInfo,
} as Record<MediaManagerApiMethod, FedOpsInteraction>;

export type UseMediaManagerApiParams = IMediaManagerSDK & { source: Source };

export const useMediaManagerApi = ({
  source,
  listItemsMP,
  searchItemsMP,
  searchFoldersMP,
  getRecentSearches,
  listFoldersMP,
  publicMediaFoldersTreeMP,
  publicItemsInfoMP,
  moveItemsToTrashMP,
  restoreItemsFromTrashMP,
  copyItemsMP,
  moveItemsMP,
  importToSiteMP,
  fetchUserInfoMP,
  updateItemMP,
  createNewFolderMP,
  emptyTrashMP,
  deleteForeverMP,
  getDownloadUrlMP,
  privateItemInfoMP,
  createNewBoardMP,
  updateBoardMP,
  removeBoardsMP,
  createZipMP,
}: UseMediaManagerApiParams): MediaManagerApi => {
  const settings = useMMGRSettings();
  const {
    mmgrSettings: {
      metaSiteToken,
      namespace,
      isPlatformizationTypesCheckEnabled,
      isPlatformizedApiEnabled,
      isPlatformizedApiLoaders2Enabled,
    },
  } = settings;

  const debug = process.env.NODE_ENV === 'development';

  const httpClient = useMemo<HttpClient | null>(() => {
    if (!metaSiteToken) {
      captureException(new Error('[useMediaManagerApi] metaSiteToken is required'));
      return null;
    }

    return new HttpClient({
      baseURL: '/_api/',
      getAppToken: () => metaSiteToken,
    });
  }, [metaSiteToken]);

  return useMemo<MediaManagerApi>(() => {
    const logParsingStarted = (methodName: MediaManagerApiMethod) => {
      fedopsLogger.interactionStarted(parsingInteractions[methodName]);
    };

    const logParsingEnded = (methodName: MediaManagerApiMethod) => {
      fedopsLogger.interactionEnded(parsingInteractions[methodName]);
    };

    const logPlatformizedRequest = (methodName: MediaManagerApiMethod) => {
      fedopsLogger.interactionStarted(requestsInteractions[methodName]);
    };

    const logPlatformizedResponse = (methodName: MediaManagerApiMethod) => {
      fedopsLogger.interactionEnded(requestsInteractions[methodName]);
    };

    return createMediaManagerApi({
      experiments: {
        typeValidationEnabled: isPlatformizationTypesCheckEnabled,
        platformizedApiEnabled: isPlatformizedApiEnabled,
        platformizedApiLoaders2Enabled: isPlatformizedApiLoaders2Enabled,
      },
      namespace: namespace || undefined,
      httpClient,
      mediaManagerSDK: {
        listItemsMP,
        searchItemsMP,
        searchFoldersMP,
        getRecentSearches,
        listFoldersMP,
        publicMediaFoldersTreeMP,
        publicItemsInfoMP,
        moveItemsToTrashMP,
        restoreItemsFromTrashMP,
        copyItemsMP,
        moveItemsMP,
        importToSiteMP,
        fetchUserInfoMP,
        updateItemMP,
        createNewFolderMP,
        emptyTrashMP,
        deleteForeverMP,
        getDownloadUrlMP,
        privateItemInfoMP,
        createNewBoardMP,
        updateBoardMP,
        removeBoardsMP,
        createZipMP,
      },
      onError: (error, methodName, data?: any) => {
        const isP13nEnabled = isPlatformizationTypesCheckEnabled && isPlatformizedApiEnabled;
        (error as ErrorWithData).data = data;
        bi.logger().report(
          mediaGalleryServerCallWithSuccessFailResult({
            result: 'fail',
            call_name: `${isP13nEnabled ? 'PLATFORM' : ''} ${methodName}`,
            errorReason: stringifyError(error),
          }),
        );
      },
      source,
      debug,
      monitoring: {
        onParsingStarted: logParsingStarted,
        onParsingEnded: logParsingEnded,
        onPlatformizedRequest: logPlatformizedRequest,
        onPlatformizedResponse: logPlatformizedResponse,
      },
    });
  }, [
    debug,
    httpClient,
    isPlatformizationTypesCheckEnabled,
    isPlatformizedApiEnabled,
    isPlatformizedApiLoaders2Enabled,
    namespace,
    source,
    listItemsMP,
    searchItemsMP,
    searchFoldersMP,
    getRecentSearches,
    listFoldersMP,
    publicMediaFoldersTreeMP,
    publicItemsInfoMP,
    moveItemsToTrashMP,
    restoreItemsFromTrashMP,
    copyItemsMP,
    moveItemsMP,
    importToSiteMP,
    fetchUserInfoMP,
    updateItemMP,
    createNewFolderMP,
    emptyTrashMP,
    deleteForeverMP,
    getDownloadUrlMP,
    privateItemInfoMP,
    createNewBoardMP,
    updateBoardMP,
    removeBoardsMP,
    createZipMP,
  ]);
};
